import { MultiPurposeTitle } from "@/components/Atoms/Title";
import { MultiPurposeParagraph } from "@/components/Atoms/Paragraph";
import Image from "@/components/Atoms/Image";
import { VariantProps, cva } from "class-variance-authority";
import QuoteSlider from "@/components/Molecules/QuoteSlider";
import { Shape } from "@/components/Atoms/Spacer";
import { string_to_slug } from "@/lib/functions";

export type BlockVariantProps = VariantProps<typeof BlockVariants>;
const BlockVariants = cva(
  `flex flex-col justify-center items-center gap-6 mx-auto w-full`,
  {
    variants: {
      layout: {
        container: `container lg:max-w-[65rem]`,
        fullWidth: `px-6`,
        featured: "container 2xl:max-w-[1360px]",
      },
      extraPadding: {
        true: "py-12 md:py-20 lg:py-24",
        false: "py-8 md:py-20 lg:py-16",
      },
    },
    defaultVariants: {
      layout: "container",
      extraPadding: false,
    },
  }
);

interface Props {
  layout: "container" | "fullWidth" | "featured";
  bgColor: any;
  textColor: any;
  textSize: string;
  quote: string;
  description: string;
  wantDescription: boolean;
  addImageAsset: boolean;
  asset: any;
  quotesNumber: boolean;
  quotesConfiguration: any;
  addSpacer: boolean;
  spacerConfiguration: any;
  addExtraPaddings?: boolean;
  id: string;
  name: string;
  hasHeadline: boolean;
  headline: string;
  nextSectionHasTopSpace?: boolean;
}

export default function Quote({
  layout,
  bgColor,
  quote,
  name,
  textColor,
  textSize,
  wantDescription,
  description,
  addImageAsset,
  asset,
  quotesNumber,
  quotesConfiguration,
  addSpacer,
  spacerConfiguration,
  addExtraPaddings,
  hasHeadline,
  headline,
  id,
  nextSectionHasTopSpace,
}: Props) {
  switch (quotesNumber) {
    case true:
      return (
        <div
          id={name ? string_to_slug(name) : ""}
          style={{ background: bgColor?.color.hex }}
          className="flex flex-col justify-center items-center m-auto w-full relative"
        >
          {addSpacer && (
            <Shape
              shape={spacerConfiguration?.customShape}
              color={bgColor}
              direction={spacerConfiguration?.spacerDirection}
              icon={[
                spacerConfiguration?.addIconElement,
                spacerConfiguration?.spacerIcon,
              ]}
              position={spacerConfiguration?.spacerPosition}
              iconPosition={spacerConfiguration?.iconPosition}
              invert={false}
            />
          )}
          <article className={`${BlockVariants({ layout, extraPadding: addExtraPaddings })} z-10 relative lg:pt-20 ${nextSectionHasTopSpace ? "pb-24 lg:pb-40" : ""}`}>
            {textSize == "default" ? (
              <>
                {hasHeadline && (
                  <MultiPurposeTitle
                    tag="h3"
                    data={headline}
                    color={textColor?.color}
                    layout={"fixedCenter"}
                    type="default"
                  />
                )}

                <MultiPurposeTitle
                  tag="h3"
                  data={quote}
                  color={textColor?.color}
                  layout={"fixedCenter"}
                  type="excerpt"
                />
              </>
            ) : textSize == "excerpt" ? (
              <>
                {hasHeadline && (
                  <MultiPurposeTitle
                    tag="h3"
                    data={headline}
                    color={textColor?.color}
                    layout={"fixedCenter"}
                    type="default"
                  />
                )}

                <MultiPurposeParagraph align={"center"} type="headline" data={quote} color={textColor?.color} />
              </>
            ) : (
              <MultiPurposeTitle
                tag="h3"
                data={quote}
                color={textColor?.color}
                layout={"fixedCenter"}
                type="default"
              />
            )}
            {wantDescription && (
              <div className="lg:max-w-[80%] text-center">
                <MultiPurposeParagraph
                  type="quoteSignature"
                  data={description}
                  color={textColor?.color}
                />
              </div>
            )}
            {addImageAsset && (
              <Image className={`max-w-[60%] lg:max-w-[30rem]`} data={asset} />
            )}
          </article>
        </div>
      );
    case false:
      return (
        <div
          id={name ? string_to_slug(name) : ""}
          style={{ background: bgColor?.color.hex }}
          className="flex flex-col justify-center items-center m-auto w-full relative"
        >
          {addSpacer && (
            <Shape
              shape={spacerConfiguration?.customShape}
              color={bgColor}
              direction={spacerConfiguration?.spacerDirection}
              icon={[
                spacerConfiguration?.addIconElement,
                spacerConfiguration?.spacerIcon,
              ]}
              position={spacerConfiguration?.spacerPosition || "bottom"}
              iconPosition={spacerConfiguration?.iconPosition}
              invert={false}
            />
          )}
          <article className={`w-full z-10 relative ${nextSectionHasTopSpace ? "pb-24 lg:pb-40" : ""}`}>
            <QuoteSlider color={textColor?.color} data={quotesConfiguration} extraPadding={addExtraPaddings} />
          </article>
        </div>
      );
    default:
      return <></>;
      break;
  }
}

export const fragment = `
    fragment SectionQuoteFragment on SectionQuoteRecord{
      __typename
      id
      name
      quote
      layout
      hasHeadline
      headline
      addSpacer
      addExtraPaddings
      textSize
      spacerConfiguration {
        id
        spacerDirection
        spacerPosition
        customShape {
          img {
            alt
            url
            width
            height
          }
        }
        addIconElement
        spacerIcon {
          img {
            alt
            url
            width
            height
          }
        }
        iconPosition
      }
      wantDescription
      description
      quotesNumber
      quotesConfiguration {
        content
        additionalContent
      }
      bgColor{
        color{
          hex
        }
      }
      textColor{
        color{
          hex
        }
      }
      addImageAsset
      asset{
        url
        width
        height
      }
  }
`;
